// components/FiltersSection.js - FIXED: All compilation errors resolved
import React, { useState, useEffect, useMemo } from 'react';
import { Search, X, Plus, Edit3 } from 'lucide-react';
import { styles } from '../styles';
import { utils } from '../utils';

const FiltersSection = ({
                            searchTerm,
                            handleSearchChange,
                            selectedTable,
                            setSelectedTable,
                            tables,
                            originFilter,
                            handleOriginFilterChange,
                            origins,
                            countryFilter,
                            handleCountryFilterChange,
                            countries,
                            accessData,
                            resetFilters,
                            // 🚨 NEW: Function to apply all filters in one request
                            applyAllFiltersDirectly
                        }) => {
    const [localSearchTerm, setLocalSearchTerm] = useState(searchTerm);
    const [searchTimeout, setSearchTimeout] = useState(null);

    // 🚨 NEW: Custom Origin Input State
    const [isCustomOriginMode, setIsCustomOriginMode] = useState(false);
    const [customOriginValue, setCustomOriginValue] = useState('');
    const [pendingCustomOrigin, setPendingCustomOrigin] = useState(''); // Value in input but not yet applied
    const [pendingCountryFilter, setPendingCountryFilter] = useState(''); // Value in country dropdown but not yet applied

    // 🚨 REMOVED: Unused operation tracking variables (they were causing compilation errors)

    // Handle local search input with 1-second delay
    const handleLocalSearchChange = (value) => {
        setLocalSearchTerm(value);

        // Clear existing timeout
        if (searchTimeout) {
            clearTimeout(searchTimeout);
        }

        // Set new timeout for 1 second
        const timeout = setTimeout(() => {
            handleSearchChange(value);
        }, 1000);

        setSearchTimeout(timeout);
    };

    // 🚨 NEW: Handle Custom Origin Input (no auto-apply)
    const handleCustomOriginInputChange = (value) => {
        setPendingCustomOrigin(value);
        // Don't apply filter automatically - wait for search button click
    };

    // 🚨 FIXED: Apply ALL pending filters when main search button is clicked - SINGLE REQUEST
    const applyAllFilters = () => {
        console.log('🔍 MANUAL SEARCH: Applying all filters in SINGLE request...', {
            pendingOrigin: pendingCustomOrigin,
            pendingCountry: pendingCountryFilter,
            currentOrigin: originFilter,
            currentCountry: countryFilter
        });

        // Calculate final filter values
        const finalOriginFilter = pendingCustomOrigin?.trim() || 'All Origins';
        const finalCountryFilter = pendingCountryFilter || countryFilter;
        const finalSearchTerm = localSearchTerm || searchTerm;

        console.log('🔍 MANUAL SEARCH: Final filters:', {
            origin: finalOriginFilter,
            country: finalCountryFilter,
            search: finalSearchTerm
        });

        // Update local state immediately
        setCustomOriginValue(finalOriginFilter === 'All Origins' ? '' : finalOriginFilter);

        // 🚨 CRITICAL: Use direct filter application if available to prevent multiple requests
        if (applyAllFiltersDirectly && typeof applyAllFiltersDirectly === 'function') {
            console.log('🔍 Using direct filter application - SINGLE REQUEST');
            applyAllFiltersDirectly(finalSearchTerm, finalOriginFilter, finalCountryFilter);
        } else {
            console.log('🔍 Fallback to individual filter changes - MAY CAUSE MULTIPLE REQUESTS');

            // Update origin filter first
            if (finalOriginFilter !== originFilter) {
                handleOriginFilterChange(finalOriginFilter);
            }

            // Update country filter
            if (finalCountryFilter !== countryFilter) {
                handleCountryFilterChange(finalCountryFilter);
            }

            // Update search term
            if (finalSearchTerm !== searchTerm) {
                handleSearchChange(finalSearchTerm);
            }
        }
    };

    // 🚨 NEW: Handle Enter key press to apply filters
    const handleCustomOriginKeyPress = (e) => {
        if (e.key === 'Enter') {
            applyAllFilters();
        }
    };

    // 🚨 FIXED: Toggle Custom Origin Mode - NO backend calls
    const toggleCustomOriginMode = () => {
        if (isCustomOriginMode) {
            // Switching back to dropdown mode - DON'T call handleOriginFilterChange
            setIsCustomOriginMode(false);
            setCustomOriginValue('');
            setPendingCustomOrigin('');
            
            // REMOVED: handleOriginFilterChange('All Origins') - this was causing auto-requests!
        } else {
            // Switching to custom input mode
            setIsCustomOriginMode(true);
            
            setCustomOriginValue('');
            setPendingCustomOrigin('');
        }
    };

    // 🚨 NEW: Handle country filter change (store as pending, don't apply immediately)
    const handleCountryFilterSelection = (value) => {
        setPendingCountryFilter(value);
        // Don't call handleCountryFilterChange immediately - wait for search button
    };

    // Sync local search with parent when searchTerm changes externally
    useEffect(() => {
        setLocalSearchTerm(searchTerm);
    }, [searchTerm]);

    // Sync pending country filter with actual country filter when it changes externally
    useEffect(() => {
        if (!pendingCountryFilter) {
            setPendingCountryFilter(countryFilter);
        }
    }, [countryFilter, pendingCountryFilter]);

    // Sync pending custom origin with actual origin filter when it changes externally
    useEffect(() => {
        if (!pendingCustomOrigin && originFilter !== 'All Origins') {
            setPendingCustomOrigin(originFilter);
        }
    }, [originFilter, pendingCustomOrigin]);

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (searchTimeout) {
                clearTimeout(searchTimeout);
            }
        };
    }, [searchTimeout]);

    const cleanOrigins = useMemo(() => {
        const cleanOriginsSet = new Set();

        origins.forEach(origin => {
            if (typeof origin === 'string') {
                const cleanOrigin = origin.trim();
                if (cleanOrigin && cleanOrigin !== '' && cleanOrigin !== 'null' && cleanOrigin !== 'undefined') {
                    cleanOriginsSet.add(cleanOrigin);
                }
            } else if (origin && typeof origin === 'object' && origin.access_origin) {
                const cleanOrigin = origin.access_origin.trim();
                if (cleanOrigin && cleanOrigin !== '' && cleanOrigin !== 'null' && cleanOrigin !== 'undefined') {
                    cleanOriginsSet.add(cleanOrigin);
                }
            }
        });

        // Convert Set to Array and sort alphabetically
        return Array.from(cleanOriginsSet).sort();
    }, [origins]);

    const cleanCountries = useMemo(() => {
        const cleanCountries = new Set();

        countries.forEach(country => {
            if (typeof country === 'string') {
                const countryName = country.trim();
                if (countryName && countryName !== '' && countryName !== 'null' && countryName !== 'undefined') {
                    cleanCountries.add(countryName);
                }
            } else if (country && typeof country === 'object' && country.access_destination) {
                const countryName = country.access_destination.trim();
                if (countryName && countryName !== '') {
                    cleanCountries.add(countryName);
                }
            }
        });

        // Convert Set to Array and sort alphabetically
        return Array.from(cleanCountries).sort();
    }, [countries]);

    // Check if any filters have pending changes
    const hasPendingChanges =
        (pendingCustomOrigin !== customOriginValue && pendingCustomOrigin !== originFilter) ||
        (pendingCountryFilter && pendingCountryFilter !== countryFilter) ||
        (localSearchTerm !== searchTerm);

    // Filter data for active filters display
    const filteredData = useMemo(
        () => utils.filterData(accessData, searchTerm, originFilter, countryFilter),
        [accessData, searchTerm, originFilter, countryFilter]
    );
    const hasActiveFilters = useMemo(
        () => utils.hasActiveFilters(searchTerm, originFilter, countryFilter),
        [searchTerm, originFilter, countryFilter]
    );

    return (
        <div style={{...styles.filtersContainer, position: 'relative', zIndex: 10000 }}>
            {/* ✅ FIXED: Filters Status and Reset */}
            {hasActiveFilters && (
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: '12px',
                    padding: '8px 12px',
                    backgroundColor: 'rgba(124, 58, 237, 0.1)',
                    border: '1px solid rgba(124, 58, 237, 0.3)',
                    borderRadius: '6px',
                    fontSize: '0.875rem'
                }}>
                    <span style={{ color: '#c4b5fd' }}>
                        Filters active: {filteredData.length} of {accessData.length} entries shown
                        {isCustomOriginMode && customOriginValue && (
                            <span style={{ marginLeft: '8px', color: '#a78bfa' }}>
                                (Custom origin: "{customOriginValue}")
                                {((pendingCustomOrigin !== customOriginValue && pendingCustomOrigin) ||
                                    (pendingCountryFilter !== countryFilter && pendingCountryFilter)) && (
                                    <span style={{ color: '#f59e0b', marginLeft: '4px' }}>
                                        - Ready to search: "{pendingCustomOrigin || pendingCountryFilter}"
                                    </span>
                                )}
                            </span>
                        )}
                    </span>
                    <button
                        onClick={() => {
                            console.log('🔍 CLEAR FILTERS: Resetting all filters and pending states');
                            resetFilters();
                            // Reset custom origin mode when clearing filters
                            setIsCustomOriginMode(false);
                            setCustomOriginValue('');
                            setPendingCustomOrigin('');
                            setPendingCountryFilter('');
                        }}
                        style={{
                            ...styles.button,
                            padding: '4px 8px',
                            fontSize: '0.75rem',
                            backgroundColor: 'rgba(124, 58, 237, 0.2)',
                            border: '1px solid rgba(124, 58, 237, 0.4)'
                        }}
                        onMouseOver={(e) => e.target.style.backgroundColor = 'rgba(124, 58, 237, 0.3)'}
                        onMouseOut={(e) => e.target.style.backgroundColor = 'rgba(124, 58, 237, 0.2)'}
                    >
                        <X size={12} />
                        Clear Filters
                    </button>
                </div>
            )}

            <div style={{...styles.filtersGrid, position: 'relative', zIndex: 10001 }}>
                {/* Search with 1-second delay */}
                <div style={styles.searchContainer}>
                    <Search
                        size={16}
                        style={styles.searchIcon}
                    />
                    <input
                        type="text"
                        placeholder="Search in Origin and Message columns..."
                        value={localSearchTerm}
                        onChange={(e) => handleLocalSearchChange(e.target.value)}
                        style={{
                            ...styles.searchInput,
                            ...(localSearchTerm !== searchTerm ? {
                                borderColor: 'rgba(255, 193, 7, 0.5)',
                                backgroundColor: 'rgba(255, 193, 7, 0.05)'
                            } : {})
                        }}
                        onFocus={(e) => e.target.style.borderColor = '#7c3aed'}
                        onBlur={(e) => e.target.style.borderColor = localSearchTerm !== searchTerm ? 'rgba(255, 193, 7, 0.5)' : 'rgba(255, 255, 255, 0.2)'}
                    />
                    {localSearchTerm !== searchTerm && (
                        <div style={{
                            position: 'absolute',
                            right: '12px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            color: '#f59e0b',
                            fontSize: '0.75rem',
                            pointerEvents: 'none'
                        }}>
                            Typing...
                        </div>
                    )}
                </div>

                {/* Table Selection */}
                <select
                    value={selectedTable}
                    onChange={(e) => setSelectedTable(e.target.value)}
                    style={styles.select}
                    onFocus={(e) => e.target.style.borderColor = '#7c3aed'}
                    onBlur={(e) => e.target.style.borderColor = 'rgba(255, 255, 255, 0.2)'}
                >
                    {tables && tables.length > 0 && tables.map(table => (
                        <option key={table.table_name} value={table.table_name} style={{ backgroundColor: '#1e293b', color: 'white' }}>
                            {table.table_name}
                        </option>
                    ))}
                </select>

                {/* 🚨 ENHANCED: Origin Filter with Custom Input Feature */}
                <div style={{ position: 'relative', display: 'flex', gap: '8px', alignItems: 'center', zIndex: 10002 }}>
                    {/* Toggle Button for Custom/Dropdown Mode */}
                    <button
                        onClick={toggleCustomOriginMode}
                        style={{
                            padding: '8px',
                            backgroundColor: isCustomOriginMode ? 'rgba(16, 185, 129, 0.2)' : 'rgba(124, 58, 237, 0.2)',
                            border: `1px solid ${isCustomOriginMode ? 'rgba(16, 185, 129, 0.4)' : 'rgba(124, 58, 237, 0.4)'}`,
                            borderRadius: '6px',
                            color: isCustomOriginMode ? '#34d399' : '#a78bfa',
                            cursor: 'pointer',
                            fontSize: '0.75rem',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            minWidth: '32px',
                            justifyContent: 'center'
                        }}
                        title={isCustomOriginMode ? 'Switch to dropdown list' : 'Switch to custom input'}
                        onMouseOver={(e) => {
                            e.target.style.backgroundColor = isCustomOriginMode ? 'rgba(16, 185, 129, 0.3)' : 'rgba(124, 58, 237, 0.3)';
                        }}
                        onMouseOut={(e) => {
                            e.target.style.backgroundColor = isCustomOriginMode ? 'rgba(16, 185, 129, 0.2)' : 'rgba(124, 58, 237, 0.2)';
                        }}
                    >
                        {isCustomOriginMode ? <Edit3 size={14} /> : <Plus size={14} />}
                    </button>

                    {/* Origin Filter - Dropdown or Custom Input */}
                    {!isCustomOriginMode ? (
                        // Standard dropdown mode WITHOUT search button
                        <select
                            value={pendingCustomOrigin || originFilter}
                            onChange={(e) => {
                                const value = e.target.value;
                                setPendingCustomOrigin(value);
                                // Don't apply immediately - wait for main search button
                            }}
                            style={{
                                ...styles.select,
                                flex: 1,
                                ...(pendingCustomOrigin && pendingCustomOrigin !== originFilter ? {
                                    backgroundColor: 'rgba(255, 193, 7, 0.1)',
                                    borderColor: 'rgba(255, 193, 7, 0.5)'
                                } : originFilter !== 'All Origins' ? {
                                    backgroundColor: 'rgba(124, 58, 237, 0.2)',
                                    borderColor: 'rgba(124, 58, 237, 0.5)'
                                } : {})
                            }}
                            onFocus={(e) => e.target.style.borderColor = '#7c3aed'}
                            onBlur={(e) => e.target.style.borderColor = pendingCustomOrigin && pendingCustomOrigin !== originFilter ? 'rgba(255, 193, 7, 0.5)' : (originFilter !== 'All Origins' ? 'rgba(124, 58, 237, 0.5)' : 'rgba(255, 255, 255, 0.2)')}
                            title="Select origin then click main Search button"
                        >
                            <option value="All Origins" style={{ backgroundColor: '#1e293b', color: 'white' }}>
                                All Origins ({cleanOrigins.length} available)
                            </option>
                            {cleanOrigins.map(origin => (
                                <option
                                    key={origin}
                                    value={origin}
                                    style={{ backgroundColor: '#1e293b', color: 'white' }}
                                >
                                    {origin}
                                </option>
                            ))}
                        </select>
                    ) : (
                        // Custom input mode - NO DROPDOWN, just input field
                        <input
                            type="text"
                            placeholder="Type custom origin (e.g., Apple, MyService)..."
                            value={pendingCustomOrigin}
                            onChange={(e) => handleCustomOriginInputChange(e.target.value)}
                            onKeyPress={handleCustomOriginKeyPress}
                            style={{
                                ...styles.select,
                                flex: 1,
                                backgroundColor: pendingCustomOrigin ? 'rgba(16, 185, 129, 0.1)' : 'rgba(255, 255, 255, 0.05)',
                                borderColor: pendingCustomOrigin !== customOriginValue && pendingCustomOrigin ? 'rgba(255, 193, 7, 0.5)' : (customOriginValue ? 'rgba(16, 185, 129, 0.5)' : 'rgba(255, 255, 255, 0.2)'),
                                color: pendingCustomOrigin ? '#34d399' : 'rgba(255, 255, 255, 0.8)',
                                ...(pendingCustomOrigin !== customOriginValue && pendingCustomOrigin ? {
                                    backgroundColor: 'rgba(255, 193, 7, 0.05)'
                                } : {})
                            }}
                            onFocus={(e) => {
                                e.target.style.borderColor = '#10b981';
                            }}
                            onBlur={(e) => {
                                e.target.style.borderColor = pendingCustomOrigin !== customOriginValue && pendingCustomOrigin ? 'rgba(255, 193, 7, 0.5)' : (customOriginValue ? 'rgba(16, 185, 129, 0.5)' : 'rgba(255, 255, 255, 0.2)');
                            }}
                        />
                    )}
                </div>

                {/* ✅ Countries Filter - Manual control without individual search button */}
                <select
                    value={pendingCountryFilter || countryFilter}
                    onChange={(e) => handleCountryFilterSelection(e.target.value)}
                    style={{
                        ...styles.select,
                        ...(pendingCountryFilter && pendingCountryFilter !== countryFilter ? {
                            backgroundColor: 'rgba(255, 193, 7, 0.1)',
                            borderColor: 'rgba(255, 193, 7, 0.5)'
                        } : countryFilter !== 'All Countries' ? {
                            backgroundColor: 'rgba(124, 58, 237, 0.2)',
                            borderColor: 'rgba(124, 58, 237, 0.5)'
                        } : {})
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#7c3aed'}
                    onBlur={(e) => e.target.style.borderColor = pendingCountryFilter && pendingCountryFilter !== countryFilter ? 'rgba(255, 193, 7, 0.5)' : (countryFilter !== 'All Countries' ? 'rgba(124, 58, 237, 0.5)' : 'rgba(255, 255, 255, 0.2)')}
                    title="Select country then click main Search button"
                >
                    <option value="All Countries" style={{ backgroundColor: '#1e293b', color: 'white' }}>
                        All Countries ({cleanCountries.length} available)
                    </option>
                    {cleanCountries.map(country => (
                        <option
                            key={country}
                            value={country}
                            style={{ backgroundColor: '#1e293b', color: 'white' }}
                        >
                            {country}
                        </option>
                    ))}
                </select>

                {/* 🚨 MAIN SEARCH BUTTON - Applies ALL pending filters */}
                <button
                    onClick={applyAllFilters}
                    disabled={!hasPendingChanges}
                    style={{
                        padding: '10px 16px',
                        backgroundColor: hasPendingChanges ? 'rgba(16, 185, 129, 0.2)' : 'rgba(107, 114, 128, 0.2)',
                        border: `1px solid ${hasPendingChanges ? 'rgba(16, 185, 129, 0.4)' : 'rgba(107, 114, 128, 0.3)'}`,
                        borderRadius: '6px',
                        color: hasPendingChanges ? '#34d399' : '#9ca3af',
                        cursor: hasPendingChanges ? 'pointer' : 'not-allowed',
                        fontSize: '0.875rem',
                        fontWeight: '600',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        minWidth: 'auto',
                        opacity: hasPendingChanges ? 1 : 0.6
                    }}
                    title={hasPendingChanges ? 'Apply all filter changes' : 'No changes to apply'}
                    onMouseOver={(e) => {
                        if (hasPendingChanges) {
                            e.target.style.backgroundColor = 'rgba(16, 185, 129, 0.3)';
                        }
                    }}
                    onMouseOut={(e) => {
                        if (hasPendingChanges) {
                            e.target.style.backgroundColor = 'rgba(16, 185, 129, 0.2)';
                        }
                    }}
                >
                    <Search size={16} />
                    Search
                </button>
            </div>

            {/* Pulse animation for analysis indicator */}
            <style>
                {`
                    @keyframes pulse {
                        0%, 100% {
                            opacity: 1;
                        }
                        50% {
                            opacity: 0.5;
                        }
                    }
                `}
            </style>
        </div>
    );
};

export default FiltersSection;
